import { useState, useContext, useEffect } from "react";
import { useFirestoreOrderBy } from "../../firebase/useFirestore";
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import firebase from "../../firebase/compat";
import { db } from "../../firebase/config";
import uuid from "react-uuid";
import { client } from "../../hooks/Client";
import saveFile from "../../hooks/SaveFile";
import DragIndicatorOutlinedIcon from "@mui/icons-material/DragIndicatorOutlined";
import { SavedIcon } from "../../StateManagment/SavedIcon";
import PairActivities from "./PairActivities";
import Table from "../common/table/Table.jsx";
import TableCell from "../common/table/TableCell.jsx";
import Modal from "../common/Modal";
import UploadProgress from "../common/UploadProgress.jsx";
import DeleteModal from "../common/DeleteModal.jsx";
import softDeleteDoc from "../../helpers/softDeleteDoc.js";
import { Auth } from "../../StateManagment/Auth";
import StorytellingMediaPreview from "./StorytellingMediaPreview";

const StorytellingMediaTab = () => {
  const [, setSaved] = useContext(SavedIcon);
  const [auth] = useContext(Auth);
  const [showAddContainer, setShowAddContainer] = useState(false);
  const [file, setFile] = useState(null);
  const [fileMeta, setFileMeta] = useState(null);
  const [dragArrayMedia, setDragArrayMedia] = useState([]);
  const [loading, setLoading] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteDocid, setDeleteDocid] = useState('');

  const media = useFirestoreOrderBy("LiveReportMedia", "Position", "asc");
  const activities = useFirestoreOrderBy("Activities", "Position", "asc");

  useEffect(() => {
    setDragArrayMedia(media || []);
  }, [media]);

  const handleDragEndMedia = (result) => {
    if (!result.destination) return;

    const items = Array.from(dragArrayMedia);
    const [movedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, movedItem);

    setDragArrayMedia(items);

    items.forEach((item, idx) => {
      db.collection("LiveReportMedia").doc(item.docid).update({ Position: idx + 1 });
    });

    setSaved("flex");
  };

  // Eerst bevestigen: het prullenbakje verwijderde de media voorheen meteen
  // bij de eerste klik, zonder vraag en zonder weg terug.
  const deleteModalHandler = (e) => {
    const docid = e.currentTarget.getAttribute("data-docid");
    if (!docid) return;
    setDeleteDocid(docid);
    setDeleteModal(true);
  };

  // Zacht verwijderen (ADR-008), zodat een misklik terug te halen is.
  const deleteMediaItem = async () => {
    await softDeleteDoc({ collection: "LiveReportMedia", docid: deleteDocid, authId: auth?.ID });
    setSaved("flex");
  };

  const mediaHandler = (e) => {
    setUploadError(null);
    setSaveError(null);
    saveFile(e, setFile, setFileMeta, setLoading, (err) => setUploadError(err?.message || 'Upload mislukt'));
  };

  const addMedia = async () => {
    // Voorheen stopte dit stil wanneer de upload nog liep of was mislukt: de
    // gebruiker klikte op opslaan en er gebeurde niets, zonder uitleg.
    if (uploadError) {
      setSaveError("De upload is mislukt. Kies opnieuw een bestand.");
      return;
    }

    if (!file || !fileMeta) {
      setSaveError(
        loading > 0
          ? "De upload is nog bezig. Wacht tot die klaar is."
          : "Kies eerst een foto of video."
      );
      return;
    }

    setSaveError(null);

    await db.collection("LiveReportMedia").doc().set({
      Media: file,
      Type: fileMeta.type,
      ID: uuid(),
      // Niet de `timestamp` uit firebase/config: die staat vast op het moment
      // dat de pagina werd geladen.
      // AI-CONTEXT: See docs/pitfalls.md#state-stale-module-level-timestamp
      Timestamp: firebase.firestore.FieldValue.serverTimestamp(),
      CompagnyID: client,
      Position: (media?.length || 0) + 1,
    });

    setShowAddContainer(false);
    setFile(null);
    setFileMeta(null);
    setLoading(0);
    setSaved("flex");
  };

  const mediaType = (type) => {
    if (!type) {
      return "Onbekend";
    }

    const parts = type.split("/");

    if (parts.includes("image")) {
      return "Afbeelding";
    }

    if (parts.includes("video")) {
      return "Video";
    }

      return "Onbekend";
  };

  return (
    <>
      <Table
        columns={[
          { key: "drag", label: "", searchable: false },
          { key: "media", label: "MEDIA" },
          { key: "type", label: "TYPE", searchable: false },
          { key: "activities", label: "ACTIVITEIT(EN)", searchable: false },
          { key: "delete", label: "VERWIJDEREN", searchable: false },
        ]}
        items={dragArrayMedia || []}
        getItemKey={(item) => item.ID}
        renderRow={(item) => (
          <>
            <TableCell>
              <DragIndicatorOutlinedIcon />
            </TableCell>
            <TableCell>
              <StorytellingMediaPreview id="table-media-item" src={item.Media} type={item.Type} />
            </TableCell>
            <TableCell>
              <p>{mediaType(item.Type)}</p>
            </TableCell>
            <TableCell>
              <PairActivities type={"LiveReportMedia"} item={item} activities={activities} setSaved={setSaved} />
            </TableCell>
            <TableCell>
              <DeleteOutlinedIcon className="table-delete-icon" data-docid={item.docid} onClick={deleteModalHandler} />
            </TableCell>
          </>
        )}
        searchAccessor={(item) => mediaType(item?.Type)}
        searchPlaceholder="Zoeken op media"
        emptyMessage="Geen media gevonden."
        draggable
        droppableId="media"
        onDragEnd={handleDragEndMedia}
        addRow={{
          toolTipContent: "Media toevoegen",
          onClick: () => setShowAddContainer(true),
        }}
      />
      <DeleteModal
        deleteModal={deleteModal}
        setDeleteModal={setDeleteModal}
        deleteItem={deleteMediaItem}
        deleteName={"dit mediabestand"}
        softDelete
      />
      <Modal
        modalOpen={showAddContainer}
        setModalOpen={setShowAddContainer}
        pageHeader={"Media toevoegen"}
        confirm={addMedia}
        content={
          <div className="add-options-container-item-container" id="storytelling-file-upload-container">
            {!loading && !uploadError && <p>Kies een foto of video om te uploaden</p>}
            <input
              type="file"
              onChange={mediaHandler}
              disabled={loading > 0 && loading < 100}
            />
            <UploadProgress
              progress={loading}
              error={uploadError}
              fileName={fileMeta?.name}
            />
            {saveError && (
              <p className="storytelling-media-save-error" role="alert">{saveError}</p>
            )}
          </div>
        }
      />
    </>
  );
};

export default StorytellingMediaTab;
